import axios from 'axios';
import React, { useState } from 'react'
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Form, Modal, Button, Progress, Icon } from 'semantic-ui-react'
import { DownloadService } from '~/api/services/downloadFiles';
import { FormFields } from '~/components/FormFields';
import notify from '~/components/Notify';
import SimpleFileUpload from '~/components/SimpleFileUpload';
import { ModalTextHeader, ProgressWrapper } from '~/pages/Dairy/CloudStorage/style';
import { formatNumberWithoutFractions, handleErrors } from '~/utils/helpers';
import ENV from '~/environment';

interface ImportFarmersProps {
  openImportFarmersModal: boolean
  handleClose: () => void
  farmersListOptions: any[]
}

const ImportFarmers: React.FC<ImportFarmersProps> = (props) => {
  const { openImportFarmersModal, handleClose, farmersListOptions } = props
  const { t } = useTranslation()

  const { handleSubmit, control, reset, setValue } = useForm({
    mode: 'onSubmit'
  });

  const [cancelToken, setCancelToken] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isBusyImporting, setIsBusyImporting] = useState(false)

  function renderProgressBar() {
    const uploadPercent = `${formatNumberWithoutFractions(uploadProgress)}%`
    return <ProgressWrapper>
      <Progress percent={uploadProgress} indicating className='custom-progress' />
      <p className='text-below-progress'>{t('Uploaded: {{uploadPercent}}', { uploadPercent })}</p>
    </ProgressWrapper>
  }

  function renderImportForm() {
    return <>
      <FormFields.Select
        control={control}
        name={'farmerGroupId'}
        selectOnBlur={false}
        label={t('Farmer groups')}
        selection
        rules={{
          required: {
            value: true,
            message: t('Please select farmer group')
          }
        }}
        placeholder={t('Select farmer group')}
        options={farmersListOptions}
      />
      <FormFields.External
        control={control}
        label={t('File')}
        rules={{
          required: {
            value: true,
            message: t('Please upload a file')
          }
        }}
        onChange={(file: File) => {
          setValue('file', file)
        }}
        name={'file'}
      >
        <SimpleFileUpload
          accept={['.xlsx']}
          sizeLimitInMB={10}
        />
      </FormFields.External>
    </>
  }

  function renderContentImport() {
    return <>
      {isBusyImporting ? renderProgressBar() : renderImportForm()}
    </>
  }

  async function handleDownloadTemplate() {
    try {
      await DownloadService.downloadFile('farmers/import/download-template', 'template')
    } catch (error) {
      handleErrors(error)
    }
  }

  async function onSubmit(data) {
    setIsBusyImporting(true)
    try {
      const source = axios.CancelToken.source();
      setCancelToken(source);
      const formData = new FormData();
      formData.append('farmerGroupId', data.farmerGroupId)
      formData.append('file', data.file)
      await axios.post(`${ENV.API_URL}${'/farmers/import'}`, formData, {
        headers: { 'Authorization': localStorage.getItem('token') },
        onUploadProgress: (progressEvent) => {
          const { loaded, total } = progressEvent;
          setUploadProgress((loaded / total) * 100);
        },
        cancelToken: source.token
      })
      reset({
        file: null,
        farmerGroupId: ''
      })
      handleClose()
      notify(t('Successfully imported data'), 'success')
    } catch (error) {
      setValue('file', null)
      handleErrors(error)
    }
    setIsBusyImporting(false)
  }

  return <Modal
    open={openImportFarmersModal}
    onClose={() => {
      if (cancelToken) {
        cancelToken.cancel(t('Upload canceled by user'))
      }
      handleClose()
    }}
    as={Form}
    onSubmit={handleSubmit(onSubmit)}
  >
    <Modal.Header style={{ display: 'flex', justifyContent: 'space-between' }}>
      <ModalTextHeader>{t('Import')}</ModalTextHeader>
      <Icon style={{ cursor: 'pointer' }} onClick={() => {
        if (cancelToken) {
          cancelToken.cancel(t('Upload canceled by user'))
        }
        reset({
          file: '',
          farmerGroupId: null
        })
        handleClose()
      }} name='close' size='large' />
    </Modal.Header>
    <Modal.Content style={{ position: 'relative' }}>
      {renderContentImport()}
    </Modal.Content>
    <Modal.Actions>
      <Button
        type='button'
        onClick={() => handleDownloadTemplate()}
        positive
      >{t('Template download')}</Button>
      <Button positive>{t('Save')}</Button>
      <Button onClick={() => {
        if (cancelToken) {
          cancelToken.cancel(t('Upload canceled by user'))
        }
        reset({
          file: '',
          farmerGroupId: null
        })
        handleClose()
      }} negative type='button'>{t('Close')}</Button>
    </Modal.Actions>
  </Modal>
}
export default ImportFarmers